import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import TextField from '@shared/components/TextField';
import { useUserDataQuery, useUpdateUserMutation } from '../../shared/queries';
import { useForm, useWatch } from 'react-hook-form';
import FormDialog from '@shared/components/FormDialog';
import PhonePrefixAutocomplete, {
  getDefaultPhonePrefix,
} from '@shared/components/phone-prefix-autocomplete';
import { useSubscriptionPlan } from '@shared/store';
import { SubscriptionPlansEnum } from '../../features/subscription/entity';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import {
  UserForm,
  parseBankCode,
  isSupportedPairingBankCode,
  supportedPairingBankNames,
  supportedPairingBanks,
} from '@features/profile/entity';
import { parsePhoneWithPrefix } from '@shared/entity';

export type ImperativeFormDialogRefType = {
  open: (fieldToFocus?: string) => void;
  close: () => void;
};

type UserProfileFormValues = UserForm & {
  phonePrefix?: string;
};

export const useGetImperativeOpen = <T extends object>(
  ref: React.Ref<ImperativeFormDialogRefType>,
) => {
  const [open, setOpen] = useState(false);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const fieldToFocusRef = useRef<string | null>(null);

  const handleInputRef = (fieldPath: keyof T) => (el: HTMLInputElement | null) => {
    inputRefs.current[fieldPath as string] = el;

    if (el && open && fieldToFocusRef.current === fieldPath) {
      el.focus();
      el.select?.();
      fieldToFocusRef.current = null;
    }
  };

  useImperativeHandle(ref, () => ({
    open: field => {
      fieldToFocusRef.current = field ?? null;
      handleClickOpen();
    },
    close: () => setOpen(false),
  }));

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
  }, [open]);

  return {
    open,
    close: handleClose,
    handleInputRef,
  };
};

export const useUserProfile = (ref?: React.Ref<ImperativeFormDialogRefType>) => {
  const { mutation: updateUser } = useUpdateUserMutation();
  const { data: userData } = useUserDataQuery();
  const plan = useSubscriptionPlan();
  const isExpertPlan = plan === SubscriptionPlansEnum.EXPERT;
  const userProfileFormConfig = getUserProfileFormConfig(isExpertPlan);
  const { open, close, handleInputRef } = useGetImperativeOpen<UserProfileFormValues>(ref!);

  const phonePrefixFromUserData = userData?.phone
    ? parsePhoneWithPrefix(userData.phone).phonePrefix
    : getDefaultPhonePrefix();

  const { control, handleSubmit, reset } = useForm<UserProfileFormValues>({
    defaultValues: {
      reviewUrl: userData?.reviewUrl,
      contactEmail: userData?.contactEmail ?? '',
      bankAccount: userData?.bankAccount,
      name: userData?.name,
      loyaltyPercent: userData?.loyaltyPercent,
      address: userData?.address,
      bio: userData?.bio,
      phone: parsePhoneWithPrefix(userData?.phone ?? '').phone,
      phonePrefix: phonePrefixFromUserData,
    },
  });

  const onSubmit = (data: UserProfileFormValues) => {
    const phonePrefix = data.phonePrefix ?? getDefaultPhonePrefix();
    const phone = data.phone ? `${phonePrefix}${data.phone}` : undefined;
    const payload: UserForm = {
      contactEmail: data.contactEmail?.trim() || null,
      reviewUrl: data.reviewUrl,
      bankAccount: data.bankAccount,
      name: data.name,
      loyaltyPercent: data.loyaltyPercent,
      address: data.address,
      bio: data.bio,
      phone,
    };

    updateUser.mutate(payload, {
      onSuccess: newData => {
        const parsedPhone = parsePhoneWithPrefix(newData.phone ?? '');
        reset({
          ...newData,
          phone: parsedPhone.phone,
          phonePrefix: parsedPhone.phonePrefix,
        });
      },
    });
    close();
  };

  return {
    close,
    onSubmit,
    open,
    handleInputRef,
    control,
    handleSubmit,
    userData,
    userProfileFormConfig,
  };
};

export const UserProfilDialog = (props: { ref: React.Ref<ImperativeFormDialogRefType> }) => {
  const { t } = useIntlTranslations();

  const { control, onSubmit, close, open, handleSubmit, userProfileFormConfig, handleInputRef } =
    useUserProfile(props.ref);

  const bankAccountValue = useWatch({ control, name: 'bankAccount' });
  const bankCode = parseBankCode(bankAccountValue);
  const bankAccountWarning =
    bankCode && !isSupportedPairingBankCode(bankCode)
      ? t('userProfile.bankAccountUnsupported', { banks: supportedPairingBankNames() })
      : undefined;

  return (
    <FormDialog<UserProfileFormValues>
      isOpen={open}
      onClose={close}
      handleSubmit={() => handleSubmit(onSubmit)}
      title={t('userProfile.editFormTitle')}
    >
      <>
        {userProfileFormConfig.map(field => {
          if (field.shouldDisplay === false) {
            return null;
          }
          const helperText =
            field.fieldPath === 'bankAccount' && bankAccountWarning
              ? bankAccountWarning
              : field.helperText
                ? t(field.helperText)
                : undefined;

          return (
            <React.Fragment key={field.fieldPath}>
              {field.fieldPath === 'phone' ? (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <PhonePrefixAutocomplete control={control} name="phonePrefix" />
                  <TextField
                    multiline={field.multiline}
                    control={control}
                    type={field.type}
                    fieldPath={field.fieldPath}
                    helperText={helperText}
                    label={t(field.label)}
                    inputRef={(el: HTMLInputElement | null) => handleInputRef(field.fieldPath)(el)}
                    fullWidth
                  />
                </Stack>
              ) : (
                <TextField
                  multiline={field.multiline}
                  control={control}
                  type={field.type}
                  fieldPath={field.fieldPath}
                  helperText={helperText}
                  label={t(field.label)}
                  inputRef={(el: HTMLInputElement | null) => handleInputRef(field.fieldPath)(el)}
                />
              )}
              {field.fieldPath === 'bankAccount' && (
                <Box sx={{ mt: -0.5, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {t('userProfile.bankPairingTutorialTitle')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('userProfile.bankPairingTutorialHint')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {supportedPairingBanks().map(bank => (
                      <Chip
                        key={bank.code}
                        size="small"
                        variant="outlined"
                        clickable={Boolean(bank.tutorialUrl)}
                        component={bank.tutorialUrl ? 'a' : 'div'}
                        href={bank.tutorialUrl}
                        target={bank.tutorialUrl ? '_blank' : undefined}
                        rel={bank.tutorialUrl ? 'noopener noreferrer' : undefined}
                        label={bank.name}
                        sx={
                          bank.tutorialUrl
                            ? {
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: 1,
                                },
                              }
                            : undefined
                        }
                        avatar={
                          bank.logo ? (
                            <Avatar
                              src={bank.logo}
                              alt={bank.name}
                              sx={{ bgcolor: 'common.white', '& img': { objectFit: 'contain' } }}
                            />
                          ) : undefined
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </>
    </FormDialog>
  );
};

const getUserProfileFormConfig = (
  isExpertPlan: boolean,
): {
  type?: React.HTMLInputTypeAttribute | undefined;
  fieldPath: keyof UserForm;
  label: string;
  shouldDisplay?: boolean;
  multiline?: boolean;
  helperText?: string;
}[] => [
  {
    type: 'text',
    fieldPath: 'name',
    label: 'userProfile.fullName',
  },
  {
    type: 'text',
    fieldPath: 'bankAccount',
    label: 'userProfile.bankAccount',
  },
  {
    type: 'text',
    fieldPath: 'reviewUrl',
    label: 'userProfile.reviewUrl',
    helperText: 'userProfile.reviewUrlHelper',
  },
  {
    type: 'number',
    label: 'userProfile.loyaltyPercent',
    fieldPath: 'loyaltyPercent',
    shouldDisplay: isExpertPlan,
  },
  {
    type: 'text',
    fieldPath: 'address',
    label: 'userProfile.address',
    helperText: 'userProfile.addressHelper',
  },
  {
    type: 'text',
    multiline: true,
    fieldPath: 'bio',
    label: 'userProfile.bio',
    helperText: 'userProfile.bioHelper',
  },
  {
    type: 'tel',
    fieldPath: 'phone',
    label: 'userProfile.phone',
  },
  {
    type: 'email',
    fieldPath: 'contactEmail',
    label: 'userProfile.contactEmail',
    helperText: 'userProfile.contactEmailHelper',
  },
];
